import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { API } from '../../shared/api/Api';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../../features/authSlice';
import { showToast } from '../../utils/utils';
import Close from '../../assets/svg/CloseButton.svg';
import Loader from '../Live/Loader';
import Datepicker from 'react-tailwindcss-datepicker';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: "easeInOut" }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05 }
    })
};

const AssignTempRole = ({ role, onClose }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateValue, setDateValue] = useState({
        startDate: null,
        endDate: null
    });
    const [timeValue, setTimeValue] = useState('');

    const user = useSelector(selectUserInformations);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get(API.Users.app_users_allUsers, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            const data = response.data;

            if (data.status === 200 && data.errorCode === 0) {
                setUsers(data.data.items);
            } else {
                showToast("Error", "Failed to fetch users", "error");
            }
        } catch (error) {
            showToast(
                "Error",
                error.response?.data?.message || "An error occurred",
                "error"
            );
        }
    }, [user.token]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await fetchUsers();
            setLoading(false);
        };
        fetchData();
    }, [fetchUsers]);

    const handleDateChange = (newValue) => {
        setDateValue(newValue);
    };

    const handleTimeChange = (e) => {
        setTimeValue(e.target.value);
    };

    const formatExpiryDateTime = () => {
        if (!dateValue.startDate || !timeValue) return null;

        // Parse the date from the datepicker
        const date = new Date(dateValue.startDate);

        // Extract time components from the time input
        const [hours, minutes] = timeValue.split(':');

        // Set the time components to the date object
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        date.setSeconds(0);

        // Format the date to match the API's expected format: "YYYY-MM-DD HH:mm:ss"
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedHours = String(date.getHours()).padStart(2, '0');
        const formattedMinutes = String(date.getMinutes()).padStart(2, '0');
        const formattedSeconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    };

    const handleSubmit = async () => {
        if (!selectedUser) {
            showToast('Error', 'Please select a user', 'error');
            return;
        }

        if (!dateValue.startDate || !timeValue) {
            showToast('Error', 'Please select both date and time', 'error');
            return;
        }

        const expiryDateTime = formatExpiryDateTime();

        if (new Date(expiryDateTime) <= new Date()) {
            showToast('Error', 'Expiry date/time must be in the future', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                user_uuid: selectedUser,
                role: role.name,
                expires_at: expiryDateTime
            };

            const response = await axios.post(
                API.Roles.app_roles_assignTemporaryRole,
                payload,
                {
                    headers: { Authorization: `Bearer ${user.token}` }
                }
            );

            if (response.data.status === 200) {
                showToast('Success', 'Temporary role assigned successfully', 'success');
                onClose();
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Assignment failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <motion.div
            className="p-6 space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
        >
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                        Assign Temporary Role: {role?.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">UUID: {role?.uuid}</p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <img className="w-6 h-6" src={Close} alt="Close" />
                </button>
            </div>

            {/* Date and Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date
                    </label>
                    <Datepicker
                        value={dateValue}
                        onChange={handleDateChange}
                        inputClassName="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        useRange={false}
                        asSingle={true}
                        displayFormat="DD/MM/YYYY"
                        minDate={new Date()}
                        placeholder="Select expiry date"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Time
                    </label>
                    <div className="relative">
                        <input
                            type="time"
                            value={timeValue}
                            onChange={handleTimeChange}
                            className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 appearance-none"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Search and Selection */}
            <div className="space-y-4">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-2.5 text-gray-400">
                        {filteredUsers.length} users
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader />
                    </div>
                ) : (
                    <motion.div
                        className="border rounded-lg overflow-hidden"
                        layout
                    >
                        <AnimatePresence>
                            {filteredUsers.map((user, index) => (
                                <motion.div
                                    key={user.uuid}
                                    className={`p-6 border-b last:border-b-0 cursor-pointer ${selectedUser === user.uuid
                                        ? 'bg-blue-50'
                                        : 'bg-white hover:bg-gray-50'
                                        }`}
                                    variants={itemVariants}
                                    custom={index}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    whileHover={{ scale: 1.02 }}
                                    onClick={() => setSelectedUser(user.uuid)}
                                >
                                    <div className="flex items-center">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 ${selectedUser === user.uuid
                                            ? 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300'
                                            }`}>
                                            {selectedUser === user.uuid && (
                                                <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{user.name}</p>
                                            <p className="text-sm text-gray-500">{user.email}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        {filteredUsers.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No users found matching your search
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 border-t pt-4">
                <motion.button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Cancel
                </motion.button>
                <motion.button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    whileHover={{ scale: submitting ? 1 : 1.05 }}
                    whileTap={{ scale: submitting ? 1 : 0.95 }}
                >
                    {submitting ? <Loader /> : 'Assign Temporary Role'}
                </motion.button>
            </div>
        </motion.div>
    );
};

export default AssignTempRole;